import {all, call, delay, fork, put, race, select, take, takeLatest} from 'redux-saga/effects'
import types from '../actions/types'
import {
  createDocumentPivotTableWatcher,
  createGoogleCompanyDocumentWatcher,
  createPivotTableWatcher,
} from 'sagas/pivotTable'
import {purchaseOrdersPivotTableKey} from 'reducers/purchasingContext/purchaseOrders'
import {getInvoiceFinancialYearStart} from 'selectors/settings'
import {getDateFilters} from 'helpers/dateRange'
import {getAllRows, getSearch} from 'selectors/pivotTable'
import {
  canAddOrderItemToPurchaseOrder,
  getCalculatedTotals,
  getPurchaseOrdersFilterParams,
  getPurchasingFilterParams,
} from 'helpers/purchasing/purchaseOrders'
import * as PurchaseOrderItemsHelper from 'helpers/purchasing/purchaseOrderItems'
import {addItems, filterDeletedItems} from 'helpers/purchasing/purchaseOrderItems'
import {generalError} from 'actions/index'
import {difference, differenceBy, get, has, isEmpty, isNil} from 'lodash'
import * as purchasingActions from '../actions/purchasing'
import {push} from 'react-router-redux'
import {
  purchaseOrderDocumentsPivotTableKey,
  purchaseOrderHistoryPivotTableKey,
  purchaseOrderInvoicesPivotTableKey,
  purchaseOrderItemsPivotTableKey,
  purchaseOrderNewDocumentsPivotTableKey,
} from 'reducers/purchasingContext/selected'
import {default as pivotTableActions, editDataMulti, updateData} from 'actions/pivotTable'
import {snackbarClear, snackbarMessage} from 'actions/snackbar'
import {getFileFormConfig, getFileFormData, getPurchaseOrderDocumentsFolderPath} from 'helpers/documents'
import {purchaseInvoicePivotTableFields} from 'sagas/invoicingSaga'
import {
  addItemsToPurchaseOrderDialogItemsPivotTableKey,
  addItemsToPurchaseOrderDialogOrdersPivotTableKey,
} from 'reducers/dialogs/addItemsToPurchaseOrderDialog'
import {quotePivotTableFields} from 'containers/quotePivotTable'
import {getAddItemsToPurchaseOrderDialog} from 'selectors/dialog'
import {getAllSubItemsForOrderItems} from 'helpers/orders'
import {getUser} from 'selectors/auth'
import {parseUserFields} from 'helpers/fields'
import {
  purchasingContextSelectorOrderItemsPivotTableKey,
  purchasingContextSelectorOrdersPivotTableKey,
} from 'reducers/purchasingContext/selector'
import iconNames from 'theme/icons'
import {getNumberFromLetterSequence} from 'helpers/string'
import {reorderItems} from 'helpers/arrays'
import FinancialHelper from 'helpers/financial'
import * as priceListActions from 'actions/priceLists'
import {partFields} from 'sagas/priceListsSaga'
import {getItemIdForUpdate} from 'helpers/pivotTable'
import {getPathForPurchaseInvoice, getPathForPurchaseOrder} from 'helpers/navigation'
import {purchasingContextPurchaseInvoiceItemsPivotTableKey} from 'reducers/purchasingContext/selectedPurchaseInvoice'
import {isPartSelectorOpen} from 'selectors/priceLists'
import {history} from '../app'
import {getCurrencies, getTaxCodes} from 'selectors/data'
import {updateTaxCodeOnOrder, updateTaxCodeOnOrderItems} from 'helpers/quotes'
import {ContactEmailType, getFormDataFor} from 'helpers/email/enum'
import {
  getDeletedPurchaseOrderItems,
  getLastPurchaseOrder,
  getLastPurchaseOrderItems,
  getPurchaseOrders,
  getPurchaseOrderSelectedState,
  getPurchaseOrderSelector,
  getSelectedCurrentTab,
  getSelectedPurchaseInvoice,
  getSelectedPurchaseOrder,
  getSelectedPurchaseOrderCurrentFolder,
  getSelectedPurchaseOrderDocumentSubFolder,
  getSelectedPurchaseOrderItems,
} from 'selectors/purchasing'
import {
  loadPurchaseOrderAcceptanceUrlFailure,
  loadPurchaseOrderAcceptanceUrlSuccess,
  purchaseOrderAddFolder,
  purchaseOrderLoadDocuments,
  purchaseOrderLoadParentFolderFailure,
  purchaseOrderLoadParentFolderSuccess,
  purchaseOrderRemoveFolderAfterIndex,
  purchaseOrderSetFoldersToRootDirectory,
  purchaseOrderUploadDocumentsFailure,
} from 'actions/purchasing'

const purchaseOrderFields = {
  default: true,
  company: {
    default: true,
    companyLogoImage: true,
    locations: true,
  },
  contact: true,
  creator: true,
  modifier: true,
  items: {
    default: true,
    part: partFields,
    partPack: {
      default: true,
      suppliers: true,
    },
    order: true,
    userfields: true,
  },
  userfields: true,
  totalInvoiced: true,
  receivedValue: true,
  printOptions: true,
}

const purchaseInvoiceFields = {
  default: true,
  company: {
    default: true,
    companyLogoImage: true,
  },
  contact: true,
  purchaseOrder: true,
  items: {
    default: true,
    purchaseOrderItem: true,
  },
  values: true,
  creator: true,
}

export default (api) => {

  function* selectPurchaseOrder({purchaseOrder}) {
    const purchaseOrderId = get(purchaseOrder, 'id', null)
    const contactId = get(purchaseOrder, 'contactId', null)
    const currentTab = yield select(getSelectedCurrentTab)
    yield put(push(getPathForPurchaseOrder(purchaseOrderId, null, contactId, currentTab)))
  }

  function* attemptLoadPurchaseOrder({purchaseOrderId}) {
    try {
      yield race({
        task: call(loadPurchaseOrder, {purchaseOrderId}),
        cancel: take(types.PURCHASING_UNLOAD_PURCHASE_ORDER),
      })
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* loadPurchaseOrder({purchaseOrderId}) {
    try {
      const response = yield call(api.purchaseOrder.getOne, purchaseOrderId, purchaseOrderFields)
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            const purchaseOrder = data.result
            const purchaseOrderItems = get(purchaseOrder, 'items', [])
            yield put(purchasingActions.loadPurchaseOrderSuccess(purchaseOrder))
            yield put(updateData(purchaseOrderItemsPivotTableKey, purchaseOrderItems))
          },
          function* onFail(key, message, code) {
            yield put(purchasingActions.loadPurchaseOrderFailure(message, code))
          }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* addPurchaseOrder({purchaseOrder}) {
    try {
      const params = {
        companyId: get(purchaseOrder, 'company.id', null),
        contactId: get(purchaseOrder, 'contact.id', null),
        description: get(purchaseOrder, 'description', null),
        currencyId: get(purchaseOrder, 'currencyId', null),
        requiredDate: get(purchaseOrder, 'requiredDate', null),
      }

      const response = yield call(api.purchaseOrder.add, params, purchaseOrderFields)
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            const purchaseOrder = data.result
            yield put(purchasingActions.addPurchaseOrderSuccess(purchaseOrder))
            yield put(purchasingActions.selectPurchaseOrder(purchaseOrder))
          },
          function* onFail(key, message, code) {
            yield put(purchasingActions.addPurchaseOrderFailure(message, code))
          }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* loadDocuments({folder}) {
    try {
      const key = purchaseOrderDocumentsPivotTableKey
      yield put(pivotTableActions.loadData(key, {}))
      const response = yield call(api.documents.getDocuments, folder.id)
      if (response.ok) {
        const {rootFolder, folders} = yield select(getPurchaseOrderSelectedState)

        // Check if we have opened any folders yet
        if (isEmpty(folders)) {
          yield put(purchaseOrderAddFolder(folder))
        } else {
          // If the folder id isn't the same as the folder we have selected add it to the list
          // else load the root directory
          if (rootFolder.id !== folder.id) {
            // Find we are navigating to previous folder remove all the folders after the one we are navigating to
            // else add the folder to the list
            if (folders.filter(function (f) {
              return f.id === folder.id
            }).length > 0) {
              const currentIndex = 1
              const index = folders.findIndex(f => f.id === folder.id) + currentIndex
              yield put(purchaseOrderRemoveFolderAfterIndex(index))
            } else {
              yield put(purchaseOrderAddFolder(folder))
            }
          } else {
            yield put(purchaseOrderSetFoldersToRootDirectory(folder))
          }
        }

        const count = get(response, 'data.data.length', 0)
        const data = get(response, 'data.data', [])
        yield put(pivotTableActions.loadDataSuccess(key, data, count))
      } else {
        yield put(pivotTableActions.loadDataFailure(key, response.message, response.code))
      }
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* loadDocumentsParentFolder({purchaseOrderId}) {
    try {
      const response = yield call(api.documents.getPurchaseOrderFolder, purchaseOrderId)
      if (response.ok) {
        const data = response.data
        yield put(purchaseOrderLoadParentFolderSuccess(data))
        const {currentFolder} = yield select(getPurchaseOrderSelectedState)
        yield put(purchaseOrderLoadDocuments(currentFolder))
      } else {
        yield put(purchaseOrderLoadParentFolderFailure(response.message, response.code))
      }
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* deleteDocuments({documentIds}) {
    try {
      yield put(snackbarMessage('Deleting your files...', 'loading', false, false))
      const response = yield call(api.documents.delete, documentIds)
      if (response.ok) {
        const currentFolder = yield select(state => getSelectedPurchaseOrderCurrentFolder(state))
        yield put(purchaseOrderLoadDocuments(currentFolder))
        const failedFiles = response.data.data.failed
        if (!isEmpty(failedFiles)) {
          const failedQty = failedFiles.length
          yield put(snackbarMessage(`There was an error deleting ${failedQty} of your files`, 'error', false))
        } else {
          yield put(snackbarMessage('Your files have been deleted', 'checkmark'))
        }
      } else {
        yield put(snackbarMessage('Your files could not be deleted: ' + response.message, 'error', false))
      }
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* uploadDocuments({documents}) {
    try {
      yield put(snackbarMessage('Uploading your files...', 'loading', false, false))
      // Get the current selected folder to send the data to
      const {currentFolder} = yield select(getPurchaseOrderSelectedState)
      const response = yield call(
          api.documents.uploadDocuments,
          currentFolder.id,
          getFileFormData(documents),
          getFileFormConfig()
      )
      if (response.ok) {
        yield put(purchaseOrderLoadDocuments(currentFolder))
        yield put(snackbarMessage('Your files have been uploaded', iconNames.tick))
      } else {
        yield put(purchaseOrderUploadDocumentsFailure(response.message, response.code))
        yield put(snackbarMessage('Your files could not be uploaded: ' + response.message, 'error', false))
      }
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* pivotTableLoaded({key, data}) {
    try {
      if (key === addItemsToPurchaseOrderDialogItemsPivotTableKey) {

        // Filter to material-types and explode sub-items
        if (data && data.length > 0) {
          const subItems = getAllSubItemsForOrderItems(data)
          const newItems = [
            ...data,
            ...subItems,
          ]
          const filteredItems = newItems.filter(canAddOrderItemToPurchaseOrder)
          yield put(updateData(key, filteredItems))
        }
      }
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* recalcTotalsFromPivotTableAction({key, recalcTotals}) {
    if (key === purchaseOrderItemsPivotTableKey) {
      if (recalcTotals !== false) yield put(purchasingActions.recalcTotals())
    }
  }

  function* changeLogWorker({callback}) {
    // grab the selected purchase order.
    const order = yield select(getSelectedPurchaseOrder)

    // now save the change - this is a bit messy but should prevent any weird update issues
    yield call(saveOrder, {silent: true, callback})

    // We grab the order items after update, because we want the correct ids from the server.
    const items = yield select(getSelectedPurchaseOrderItems)

    // compose the change action - reducers will store the result
    yield put(purchasingActions.logOrderChange(order, items))
  }

  function* changeUndoWorker() {
    try {
      const currentPurchaseOrderItems = yield select(getSelectedPurchaseOrderItems) || []
      const previousPurchaseOrder = yield select(getLastPurchaseOrder)
      const previousPurchaseOrderItems = yield select(getLastPurchaseOrderItems) || []

      // find any added items and mark them as deleted
      const addedPurchaseOrderItems =
          differenceBy(currentPurchaseOrderItems, previousPurchaseOrderItems, item => item.id)
      if (addedPurchaseOrderItems.length > 0) {
        yield put(purchasingActions.deleteOrderItems(addedPurchaseOrderItems))
      }

      // put the state back
      yield put(purchasingActions.injectSelectedOrderItems(previousPurchaseOrderItems))
      yield put(purchasingActions.undoOrderChangeData(previousPurchaseOrder))

      // now save the change
      yield put(purchasingActions.savePurchaseOrder(true))

    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* recalcTotals({order}) {
    try {
      if (isNil(order)) order = yield select(getSelectedPurchaseOrder)
      const items = yield select(state => getAllRows(state, purchaseOrderItemsPivotTableKey) || [])
      const user = yield select(getUser)

      order = getCalculatedTotals(order, items, user)
      yield put(purchasingActions.updatePurchaseOrder(order))
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* orderChanged() {
    // The server is notifying us that the PO we are observing has been changed by someone else or some other process
    const purchaseOrder = yield select(getSelectedPurchaseOrder)
    if (purchaseOrder) yield put(purchasingActions.loadPurchaseOrder(purchaseOrder.id))
  }

  function* orderSaveSuccess({data}) {
    // Inject the result to update the ids of the order items.
    yield put(purchasingActions.injectSelectedOrderItems(data.result.items || []))
  }

  function* orderSaveReceived({data}) {
    // TODO: We need to add a check here to ensure we only respond to the latest sent update
    // unless this is received from another user. Previous use of a hash check was flawed as
    // it relied on the response from the save request to be returned AFTER the web-socket
    // message, which wasn't always the case.
    yield put(purchasingActions.savePurchaseOrderSuccess(data))
  }

  function* orderSaveAdvisories({data}) {
    try {
      if (data && data.length > 0) {
        yield put(snackbarMessage(data[0], 'error', false))
      }
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  const mapItemsForSave = items => items.map(item => ({
    ...item,
    id: item.isNew ? -1 : item.id,
    partType: get(item, 'partType.id', 0),
  }))

  function* saveOrder({silent, callback}) {

    silent = (isNil(silent)) ? false : silent

    try {
      const statePurchaseOrder = yield select(getSelectedPurchaseOrder)
      const purchaseOrder = {...statePurchaseOrder}

      if (purchaseOrder) {
        delete purchaseOrder.creator
        delete purchaseOrder.modifier
        delete purchaseOrder.company
        delete purchaseOrder.contact

        let items = yield select(getSelectedPurchaseOrderItems)
        const userFields = parseUserFields(purchaseOrder)

        const deletedItems = yield select(getDeletedPurchaseOrderItems)
        items = mapItemsForSave(filterDeletedItems(items, deletedItems))

        const printOptions = get(purchaseOrder, 'printOptions', null)

        if (silent === false)
          yield put(snackbarMessage("Saving your changes...", "loading", false, false))

        const params = {purchaseOrder, items, deletedItems, userFields, printOptions, commit: true}
        const response = yield call(api.purchaseOrder.update, params, purchaseOrderFields)
        yield api.handleResponse(
            response,
            function* onOk() {

              if (silent === false)
                yield put(snackbarMessage('Your changes have been saved', iconNames.tick, true))

              if (callback) {
                yield delay(300)
                callback()
              }
            },
            function* onFail(key, message, code) {
              yield put(purchasingActions.savePurchaseOrderFailure(message, code))
            }
        )
      }
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* changeOrderStatus({
                                purchaseOrderId,
                                status,
                                contactId,
                                expectedDate,
                                requiredDate,
                                callbackOnSuccess,
                              }) {
    try {
      yield put(snackbarMessage('Saving your changes...', 'loading', false, false))
      const params = {
        purchaseOrderId,
        status,
        contactId,
        expectedDate,
        requiredDate,
      }

      const response = yield call(api.purchaseOrder.changeStatus, params, purchaseOrderFields)
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            yield put(snackbarClear())

            const currentOrder = yield select(getSelectedPurchaseOrder)
            const userId = yield select(state => getUser(state).id)
            const observingUsers = yield select(state => state.purchasingContext.selected.observingUsers)

            // if not observing purchase order call success, otherwise
            // success will be dispatched through the web socket
            if (!(currentOrder && currentOrder.id === purchaseOrderId && observingUsers.hasOwnProperty(userId))) {
              yield put(purchasingActions.changePurchaseOrderStatusSuccess({data}))
            }

            yield put(pivotTableActions.editEntity(purchaseOrdersPivotTableKey, data.result))
            if (callbackOnSuccess) callbackOnSuccess(data.result)
          },
          function* onFail(key, message, code) {
            yield put(purchasingActions.changePurchaseOrderStatusFailure({message, code}))
          }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* addPurchaseOrderItems({items}) {
    try {
      const currentItems = yield select(state => getAllRows(state, purchaseOrderItemsPivotTableKey))
      const newItems = addItems(currentItems, items)

      // TODO: Recalculate costs for these items based on the supplier (see #7802)

      yield put(updateData(purchaseOrderItemsPivotTableKey, newItems))
      yield put(purchasingActions.recalcTotals())

    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* addPurchaseOrderItem({orderItem, closeDrawer, showMessage, showPartSelector}) {
    yield put(pivotTableActions.addEntity(purchaseOrderItemsPivotTableKey, orderItem))
    if (showMessage) {
      yield put(snackbarMessage(`Added ${orderItem.quantity}x ${orderItem.partCode} to order`, iconNames.tick))
    }
    const selectorOpen = yield select(isPartSelectorOpen)

    if (closeDrawer) {
      // Close selector too (item drawer will close)
      if (selectorOpen) yield put(priceListActions.hideSelector())
    } else {
      // Close item drawer, but keep selector open
      if (selectorOpen)
        yield put(purchasingActions.hideItemDrawer())

      // If selector is not open, re-open it
      if (!selectorOpen && showPartSelector) yield put(priceListActions.showSelector())
    }
  }

  function* addPurchaseOrderItemAtIndex({orderItem, index}) {
    yield put(pivotTableActions.addEntityAtIndex(purchaseOrderItemsPivotTableKey, orderItem, index))
  }

  function* editPurchaseOrderItem({orderItem, index}) {
    yield put(pivotTableActions.editEntity(purchaseOrderItemsPivotTableKey, orderItem, index))
    yield put(purchasingActions.recalcTotals())
  }

  function* copyPurchaseOrderItem({orderItem, recalcTotals = true}) {
    const newOrderItem = PurchaseOrderItemsHelper.copyOrderItem(orderItem)
    yield put(pivotTableActions.addEntity(purchaseOrderItemsPivotTableKey, newOrderItem, {recalcTotals}))
  }

  function* copyPurchaseOrderItems({orderItems, recalcTotals = true}) {
    if (!isNil(orderItems)) {
      for (const i in orderItems) {
        const orderItem = orderItems[i]
        const newOrderItem = PurchaseOrderItemsHelper.copyOrderItem(orderItem)
        yield put(purchasingActions.copyOrderItem(newOrderItem, false))
      }
      if (recalcTotals) yield put(purchasingActions.recalcTotals())
    }
  }

  function* deletePurchaseOrderItem({orderItem}) {
    let items = yield select(state => getAllRows(state, purchaseOrderItemsPivotTableKey))
    items = items
        .filter(item => item !== orderItem)
        .map((item, itemIndex) => ({
          ...item,
          lineItem: itemIndex + 1,
          lineItemLetter: getNumberFromLetterSequence(itemIndex + 1),
        }))

    yield put(purchasingActions.updateOrderItems(items))
  }

  function* deletePurchaseOrderItems({orderItems}) {
    const items = yield select(state => getAllRows(state, purchaseOrderItemsPivotTableKey))
    const remainingItems = difference(items, orderItems)
        .map((item, itemIndex) => {
          return {
            ...item,
            lineItem: itemIndex + 1,
            lineItemLetter: getNumberFromLetterSequence(itemIndex + 1),
          }
        })

    yield put(purchasingActions.updateOrderItems(remainingItems))
  }

  function* selectAllOrderItems() {
    let selectedOrderItems = yield select(state => getAllRows(state, purchaseOrderItemsPivotTableKey))
    selectedOrderItems = selectedOrderItems.map((orderItem, index) => ({
      ...orderItem,
      selected: true,
      index,
    }))
    yield put(pivotTableActions.selectAllRows(purchaseOrderItemsPivotTableKey))
    yield put(purchasingActions.updateSelectedOrderItems(selectedOrderItems))
  }

  function* deSelectAllOrderItems() {
    let selectedOrderItems = yield select(state => getAllRows(state, purchaseOrderItemsPivotTableKey))
    selectedOrderItems = selectedOrderItems.map((orderItem, index) => ({
      ...orderItem,
      selected: false,
      index,
    }))

    yield put(pivotTableActions.deSelectAllRows(purchaseOrderItemsPivotTableKey))
    yield put(purchasingActions.updateSelectedOrderItems(selectedOrderItems))
    yield put(purchasingActions.removeSelectedOrderItems())
  }

  function* toggleSelectedOrderItem({orderItem, index}) {
    if (!isNil(orderItem)) {
      yield put(pivotTableActions.toggleSelectedEntity(purchaseOrderItemsPivotTableKey, orderItem, index))

      if (has(orderItem, 'selected') && orderItem['selected']) {
        yield put(purchasingActions.selectOrderItem(orderItem, index))
      } else {
        yield put(purchasingActions.deSelectOrderItem(orderItem, index))
      }
    }
  }

  function* reorderOrderItem({fromIndex, toIndex}) {
    const data = yield select(state => getAllRows(state, purchaseOrderItemsPivotTableKey))
    const newItems = reorderItems(data, fromIndex, toIndex).map((e, i) => ({
      ...e,
      index: i,
      lineItem: i + 1,
      lineItemLetter: getNumberFromLetterSequence(e.lineItem),
    }))
    yield put(pivotTableActions.setEntities(purchaseOrderItemsPivotTableKey, newItems))
    yield put(purchasingActions.recalcTotals())
  }

  function* updateSelectedOrderItems({selectedOrderItems}) {
    for (const orderItem in selectedOrderItems) {
      const newOrderItem = selectedOrderItems[orderItem]
      yield put(pivotTableActions.editEntity(purchaseOrderItemsPivotTableKey, newOrderItem, newOrderItem.index))
    }
    yield put(purchasingActions.recalcTotals())
  }

  function* updateOrderItems({orderItems}) {
    const updatedOrderItems = orderItems.map(orderItem => {
      return {
        ...orderItem,
        id: getItemIdForUpdate(orderItem),
      }
    })

    yield put(pivotTableActions.updateData(purchaseOrderItemsPivotTableKey, updatedOrderItems))
    yield put(purchasingActions.recalcTotals())
  }

  function* setDiscountPc({discountPc}) {
    try {
      let order = yield select(getSelectedPurchaseOrder)
      if (order) {
        order = {
          ...order,
          discountPc: FinancialHelper.round(discountPc, 2),
        }
        yield put(purchasingActions.recalcTotals(order))
      }
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* sendPurchaseOrderEmail({emailDetails}) {

    try {

      const formData = getFormDataFor(emailDetails, ContactEmailType.PURCHASE_ORDER_TEMPLATE)
      yield put(snackbarMessage("Sending your purchase order...", "loading", false, false))
      const response = yield call(api.purchaseOrder.sendEmail, formData)
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            yield put(purchasingActions.sendPurchaseOrderEmailSuccess(data))
            yield put(snackbarMessage('Your purchase order has been sent', iconNames.tick, true))
          },
          function* onFail(key, message, code) {
            yield put(generalError(message))
            yield put(purchasingActions.sendPurchaseOrderEmailFailure(message, code))
          }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* movePurchaseOrder({purchaseOrder, companyId, contactId}) {
    try {
      const currentTab = yield select(getSelectedCurrentTab)
      const callback = () =>
          history.push(getPathForPurchaseOrder(purchaseOrder.id, companyId, contactId, currentTab))

      yield put(purchasingActions.updatePurchaseOrder({
        ...purchaseOrder,
        companyId,
        contactId,
      }, callback))

    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* copyPurchaseOrder({purchaseOrder, copyFromId}) {
    try {
      const params = {
        copyFromId,
        companyId: get(purchaseOrder, 'company.id', null),
        contactId: get(purchaseOrder, 'contact.id', null),
        description: get(purchaseOrder, 'description', null),
        currencyId: get(purchaseOrder, 'currencyId', null),
        requiredDate: get(purchaseOrder, 'requiredDate', null),
      }

      const response = yield call(api.purchaseOrder.copy, params, purchaseOrderFields)
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            const purchaseOrder = data.result
            yield put(purchasingActions.copyPurchaseOrderSuccess(purchaseOrder))
            yield put(purchasingActions.selectPurchaseOrder(purchaseOrder))
          },
          function* onFail(key, message, code) {
            yield put(purchasingActions.copyPurchaseOrderFailure(message, code))
          }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* selectPurchaseInvoice({purchaseInvoice}) {
    const purchaseInvoiceId = get(purchaseInvoice, 'id', null)
    const companyId = get(purchaseInvoice, 'companyId', null)
    const contactId = get(purchaseInvoice, 'contactId', null)
    yield put(push(getPathForPurchaseInvoice(purchaseInvoiceId, companyId, contactId)))
  }

  function* loadPurchaseInvoice({purchaseInvoiceId}) {
    try {

      const response = yield call(api.purchaseInvoice.getOne, purchaseInvoiceId, purchaseInvoiceFields)
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            const purchaseInvoice = data.result
            const purchaseInvoiceItems = get(purchaseInvoice, 'items', [])
            yield put(purchasingActions.loadPurchaseInvoiceSuccess(purchaseInvoice))
            yield put(updateData(purchasingContextPurchaseInvoiceItemsPivotTableKey, purchaseInvoiceItems))
          },
          function* onFail(key, message, code) {
            yield put(purchasingActions.loadPurchaseInvoiceFailure(message, code))
          }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* addPurchaseInvoice({purchaseOrder, invoiceCreateType, isCreditNote, supplierRef, issuedDate}) {
    try {
      const params = {
        purchaseOrderId: get(purchaseOrder, 'id', null),
        companyId: get(purchaseOrder, 'companyId', null),
        contactId: get(purchaseOrder, 'contactId', null),
        invoiceCreateType,
        isCreditNote,
        supplierRef,
        issuedDate: isNil(issuedDate)
            ? new Date().getTime()
            : issuedDate,
      }

      const response = yield call(api.purchaseInvoice.add, params, purchaseInvoiceFields)
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            const purchaseInvoice = data.result
            const purchaseInvoiceItems = get(purchaseInvoice, 'items', [])
            yield put(purchasingActions.addPurchaseInvoiceSuccess(purchaseInvoice))
            yield put(updateData(purchasingContextPurchaseInvoiceItemsPivotTableKey, purchaseInvoiceItems))
            yield put(purchasingActions.selectPurchaseInvoice(purchaseInvoice))
          },
          function* onFail(key, message, code) {
            yield put(purchasingActions.addPurchaseInvoiceFailure(message, code))
          }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* markPurchaseOrderInvoiced({purchaseOrder}) {
    const params = {
      purchaseOrderIds: [purchaseOrder.id],
    }
    const response = yield call(api.purchaseOrder.markInvoiced, params)
    yield api.handleResponse(
        response,
        function* onOk() {
          yield put(purchasingActions.updatePurchaseOrder(purchaseOrder))
        },
        function* onFail(key, message) {
          yield put(snackbarMessage(message, 'error'))
        }
    )
  }

  function* markPurchaseOrderReceived({purchaseOrder}) {
    const purchaseOrderId = get(purchaseOrder, 'id', null)
    const params = {
      purchaseOrderIds: [purchaseOrderId],
    }
    const response = yield call(api.purchaseOrder.markReceived, params)
    yield api.handleResponse(
        response,
        function* onOk() {
         yield put(purchasingActions.loadPurchaseOrder(purchaseOrderId))
        },
        function* onFail(key, message) {
          yield put(snackbarMessage(message, 'error'))
        }
    )
  }

  function* updatePurchaseInvoice({purchaseInvoice}) {
    try {
      const params = {
        purchaseInvoice,
      }

      const response = yield call(api.purchaseInvoice.update, params, purchaseInvoiceFields)
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            const purchaseInvoice = data.result
            const items = get(purchaseInvoice, 'items', [])
            yield put(updateData(purchasingContextPurchaseInvoiceItemsPivotTableKey, items))
          },
          function* onFail(key, message, code) {
            yield put(purchasingActions.updatePurchaseInvoiceFailure(message, code))
          }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* updatePurchaseInvoiceFromJob({purchaseInvoiceId, invoiceCreateType}) {
    try {
      const params = {
        purchaseInvoiceId,
        invoiceCreateType,
      }

      const response = yield call(api.purchaseInvoice.updateFromJob, params, purchaseInvoiceFields)
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            const purchaseInvoice = data.result
            const items = get(purchaseInvoice, 'items', [])
            yield put(updateData(purchasingContextPurchaseInvoiceItemsPivotTableKey, items))
          },
          function* onFail(key, message, code) {
            yield put(purchasingActions.updatePurchaseInvoiceFromJobFailure(message, code))
          }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* loadAcceptanceUrl({purchaseOrderId, printTemplateId}) {
    try {
      const response = yield call(api.purchaseOrder.acceptanceUrl, purchaseOrderId, printTemplateId)
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            yield put(loadPurchaseOrderAcceptanceUrlSuccess(data.result))
          },
          function* onFail(key, message, code) {
            yield put(loadPurchaseOrderAcceptanceUrlFailure(message, code))
          }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* reorderPurchaseInvoiceItem({fromIndex, toIndex}) {
    try {
      const data = yield select(state => getAllRows(state, purchasingContextPurchaseInvoiceItemsPivotTableKey))
      const newItems = reorderItems(data, fromIndex, toIndex).map((e, i) => ({
        ...e,
        sortOrder: i + 1,
        lineItemLetter: getNumberFromLetterSequence(i + 1),
      }))
      yield put(pivotTableActions.setEntities(purchasingContextPurchaseInvoiceItemsPivotTableKey, newItems))

      const selectedPurchaseInvoice = yield select(getSelectedPurchaseInvoice)
      const updatedPurchaseInvoice = {...selectedPurchaseInvoice}
      updatedPurchaseInvoice['items'] = newItems
      yield put(purchasingActions.updatePurchaseInvoice(updatedPurchaseInvoice))

    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* addPurchaseOrderHistory({params}) {
    try {
      const response = yield call(api.purchaseOrderHistory.add, params)
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            yield put(purchasingActions.addPurchaseOrderHistorySuccess(data))
          },
          function* onFail(key, message, code) {
            yield put(purchasingActions.addPurchaseOrderHistoryFailure(message, code))
          }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* sendSignRequest({emailDetails}) {

    try {

      const formData = getFormDataFor(emailDetails, ContactEmailType.PURCHASE_ORDER_ACCEPTANCE)
      yield put(snackbarMessage("Sending your signature request...", "loading", false, false))
      const response = yield call(api.purchaseOrder.sendAcceptance, formData)
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            yield put(purchasingActions.sendSignRequestSuccess(data))
            yield put(snackbarMessage("Your signature request has been sent", iconNames.tick, true))
          },
          function* onFail(key, message, code) {
            yield put(purchasingActions.sendSignRequestFailure(message, code))
          }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* setTaxCode({taxCodeId, updateItems}) {
    try {
      const purchaseOrder = yield select(getSelectedPurchaseOrder)
      const taxCodes = yield select(getTaxCodes)
      const currencies = yield select(getCurrencies)
      const user = yield select(getUser)

      const taxCode = taxCodes.find(taxCode => taxCode.id === taxCodeId)
      if (!taxCode) return

      if (updateItems) {
        // Update both order AND items, then recalculate totals
        const orderItems = yield select(getSelectedPurchaseOrderItems)
        const updatedOrderItems = updateTaxCodeOnOrderItems(orderItems, taxCode, currencies)

        // Update items in the pivot table WITHOUT triggering recalcTotals
        yield put(editDataMulti(purchaseOrderItemsPivotTableKey, updatedOrderItems, false, {recalcTotals: false}))

        // Get items from state (now updated with new tax codes)
        const items = yield select(state => getAllRows(state, purchaseOrderItemsPivotTableKey) || [])

        // Calculate totals with the updated items and order
        const updatedOrder = updateTaxCodeOnOrder(purchaseOrder, taxCode)
        const orderWithTotals = getCalculatedTotals(updatedOrder, items, user)

        // Single update with everything (triggers ONE save with both order and items)
        yield put(purchasingActions.updatePurchaseOrder(orderWithTotals))
      } else {
        // Just update the order (no items to recalculate)
        yield put(purchasingActions.updatePurchaseOrder(updateTaxCodeOnOrder(purchaseOrder, taxCode)))
      }
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* myWatcher() {
    yield all([
      takeLatest(types.PURCHASING_SELECT_PURCHASE_ORDER, selectPurchaseOrder),
      takeLatest(types.PURCHASING_LOAD_PURCHASE_ORDER_ATTEMPT, attemptLoadPurchaseOrder),
      takeLatest(types.PURCHASING_ADD_PURCHASE_ORDER_ATTEMPT, addPurchaseOrder),
      takeLatest(types.PURCHASING_PURCHASE_ORDER_LOAD_DOCUMENTS, loadDocuments),
      takeLatest(types.PURCHASING_PURCHASE_ORDER_DOCUMENTS_LOAD_PARENT_FOLDER_ATTEMPT, loadDocumentsParentFolder),
      takeLatest(types.PURCHASING_PURCHASE_ORDER_DELETE_DOCUMENTS_ATTEMPT, deleteDocuments),
      takeLatest(types.PURCHASING_PURCHASE_ORDER_UPLOAD_DOCUMENTS_ATTEMPT, uploadDocuments),
      takeLatest(types.PIVOT_TABLE_LOAD_SUCCESS, pivotTableLoaded),
      takeLatest(types.PURCHASING_PURCHASE_ORDER_CHANGE_STATUS_ATTEMPT, changeOrderStatus),
      takeLatest(types.PURCHASING_PURCHASE_ORDER_RECALC_TOTALS, recalcTotals),
      takeLatest(types.PURCHASING_PURCHASE_ORDER_SAVE_ATTEMPT, saveOrder),
      takeLatest(types.PURCHASING_PURCHASE_ORDER_SAVE_SUCCESS, orderSaveSuccess),
      takeLatest(types.PURCHASING_PURCHASE_ORDER_SAVE_RECEIVED, orderSaveReceived),
      takeLatest(types.PURCHASING_PURCHASE_ORDER_SAVE_ADVISORIES, orderSaveAdvisories),
      takeLatest(types.PURCHASING_PURCHASE_ORDER_CHANGED, orderChanged),
      takeLatest(types.PURCHASING_PURCHASE_ORDER_UPDATE_ATTEMPT, changeLogWorker),
      takeLatest(types.PURCHASING_PURCHASE_ORDER_UPDATE_PROPERTY, changeLogWorker),
      takeLatest(types.PURCHASING_UNDO_CHANGE, changeUndoWorker),

      takeLatest(types.PURCHASING_PURCHASE_ORDER_ADD_ITEMS, addPurchaseOrderItems),
      takeLatest(types.PURCHASING_PURCHASE_ORDER_ADD_ORDER_ITEM, addPurchaseOrderItem),
      takeLatest(types.PURCHASING_PURCHASE_ORDER_ADD_ORDER_ITEM_AT_INDEX, addPurchaseOrderItemAtIndex),
      takeLatest(types.PURCHASING_PURCHASE_ORDER_EDIT_ORDER_ITEM, editPurchaseOrderItem),
      takeLatest(types.PURCHASING_PURCHASE_ORDER_COPY_ORDER_ITEM, copyPurchaseOrderItem),
      takeLatest(types.PURCHASING_PURCHASE_ORDER_COPY_ORDER_ITEMS, copyPurchaseOrderItems),
      takeLatest(types.PURCHASING_PURCHASE_ORDER_DELETE_ORDER_ITEM, deletePurchaseOrderItem),
      takeLatest(types.PURCHASING_PURCHASE_ORDER_DELETE_ORDER_ITEMS, deletePurchaseOrderItems),
      takeLatest(types.PURCHASING_PURCHASE_ORDER_SELECT_ALL_ORDER_ITEMS, selectAllOrderItems),
      takeLatest(types.PURCHASING_PURCHASE_ORDER_DESELECT_ALL_ORDER_ITEMS, deSelectAllOrderItems),
      takeLatest(types.PURCHASING_PURCHASE_ORDER_UPDATE_SELECTED_ORDER_ITEMS, updateSelectedOrderItems),
      takeLatest(types.PURCHASING_PURCHASE_ORDER_TOGGLE_SELECTED_ENTITY, toggleSelectedOrderItem),
      takeLatest(types.PURCHASING_PURCHASE_ORDER_REORDER_ORDER_ITEM, reorderOrderItem),
      takeLatest(types.PURCHASING_PURCHASE_ORDER_UPDATE_ITEMS, updateOrderItems),
      takeLatest(types.PURCHASING_PURCHASE_ORDER_SET_DISCOUNT_PC, setDiscountPc),

      // Listen to inline pivot table changes
      // IMPORTANT! It is significant that we are specifically listening to
      // addEntity and editData and NOT anything else. These two are invoked
      // PRIOR to committing changes, whereas some other calls, such as
      // updateData are invoked AFTER committing changes
      // (see actions/purchasing.js:injectSelectedOrderItems) and so responding
      // to that here causes an infinite loop.
      takeLatest(types.PIVOT_TABLE_ADD_ENTITY, recalcTotalsFromPivotTableAction),
      takeLatest(types.PIVOT_TABLE_EDIT_DATA, recalcTotalsFromPivotTableAction),
      takeLatest(types.PIVOT_TABLE_EDIT_DATA_MULTI, recalcTotalsFromPivotTableAction),
      takeLatest(types.PURCHASING_PURCHASE_ORDER_SEND_EMAIL_ATTEMPT, sendPurchaseOrderEmail),
      takeLatest(types.PURCHASING_PURCHASE_ORDER_MOVE, movePurchaseOrder),
      takeLatest(types.PURCHASING_SELECT_PURCHASE_INVOICE, selectPurchaseInvoice),
      takeLatest(types.PURCHASING_PURCHASE_ORDER_COPY_ATTEMPT, copyPurchaseOrder),
      takeLatest(types.PURCHASING_LOAD_PURCHASE_INVOICE_ATTEMPT, loadPurchaseInvoice),
      takeLatest(types.PURCHASING_PURCHASE_INVOICE_ADD_ATTEMPT, addPurchaseInvoice),
      takeLatest(types.PURCHASE_ORDER_MARK_INVOICED_ATTEMPT, markPurchaseOrderInvoiced),
      takeLatest(types.PURCHASE_ORDER_MARK_RECEIVED_ATTEMPT, markPurchaseOrderReceived),
      takeLatest(types.PURCHASING_PURCHASE_INVOICE_UPDATE_ATTEMPT, updatePurchaseInvoice),
      takeLatest(types.PURCHASING_PURCHASE_INVOICE_UPDATE_FROM_JOB_ATTEMPT, updatePurchaseInvoiceFromJob),
      takeLatest(types.PURCHASING_PURCHASE_INVOICE_REORDER_PURCHASE_INVOICE_ITEM, reorderPurchaseInvoiceItem),
      takeLatest(types.PURCHASING_PURCHASE_ORDER_SET_TAX_CODE, setTaxCode),
      takeLatest(types.PURCHASING_LOAD_PURCHASE_ORDER_ACCEPTANCE_URL_ATTEMPT, loadAcceptanceUrl),
      takeLatest(types.PURCHASING_PURCHASE_ORDER_SEND_SIGN_REQUEST_ATTEMPT, sendSignRequest),
      takeLatest(types.ADD_PURCHASE_ORDER_HISTORY_ATTEMPT, addPurchaseOrderHistory),
    ])
  }

  const purchaseOrdersPivotTable = createPivotTableWatcher(
      api.purchaseOrder,
      purchaseOrdersPivotTableKey,
      [
        types.PURCHASING_PURCHASE_ORDER_UPDATE_FILTERS,
      ],
      state => {
        const invoiceFinancialYearStart = getInvoiceFinancialYearStart(state)
        const filters = getPurchaseOrders(state)
        const {targetStart, targetEnd} = getDateFilters(filters, invoiceFinancialYearStart)
        const {status, companies, raisedBy, printedEmailed, dateTypes} = filters
        const searchTerm = getSearch(state, purchaseOrdersPivotTableKey)

        return getPurchaseOrdersFilterParams(
            searchTerm,
            status,
            companies,
            raisedBy,
            printedEmailed,
            dateTypes,
            targetStart,
            targetEnd)
      },
      {
        default: true,
        company: true,
        contact: true,
        creator: true,
        modifier: true,
        userfields: true,
        totalInvoiced: true,
        receivedValue: true,
      })

  const purchaseOrderItemsPivotTable = createPivotTableWatcher(
      api.purchaseOrderItem,
      purchaseOrderItemsPivotTableKey,
      [],
      state => {
        const purchaseOrder = getSelectedPurchaseOrder(state)
        return {
          purchaseOrderId: [
            get(purchaseOrder, 'id', null),
          ],
        }
      },
      {
        default: true,
        part: true,
        partPack: true,
        creator: true,
        modifier: true,
      }
  )

  const purchaseOrderDocumentsPivotTable = createGoogleCompanyDocumentWatcher(
      api.documents,
      purchaseOrderDocumentsPivotTableKey,
      [
        types.PURCHASING_PURCHASE_ORDER_DOCUMENTS_UPDATE_FILTERS,
      ],
      state => {
        const {purchaseOrder, currentFolder} = getPurchaseOrderSelectedState(state)
        return {
          folderId: get(currentFolder, 'id'),
          purchaseOrderId: get(purchaseOrder, 'id', null),
        }
      }
  )

  const purchaseOrderNewDocumentsPivotTable = createDocumentPivotTableWatcher(
      api.document,
      purchaseOrderNewDocumentsPivotTableKey,
      [types.PURCHASE_ORDERS_DOCUMENTS_SET_SELECTED_SUB_FOLDER],
      state => {
        const purchaseOrder = getSelectedPurchaseOrder(state)
        const baseFolder = getPurchaseOrderDocumentsFolderPath(purchaseOrder)
        const subFolder = getSelectedPurchaseOrderDocumentSubFolder(state) || ''
        return {
          documentProvider: "DEFAULT",
          folderPath: baseFolder + subFolder,
          fromRoot: false,
        }
      }
  )

  const purchaseOrderInvoicesPivotTable = createPivotTableWatcher(
      api.purchaseInvoice,
      purchaseOrderInvoicesPivotTableKey,
      [],
      state => {
        const purchaseOrder = getSelectedPurchaseOrder(state)
        return {
          purchaseOrderId: [
            get(purchaseOrder, 'id', null),
          ],
        }
      },
      purchaseInvoicePivotTableFields
  )

  const purchaseOrderHistoryPivotTable = createPivotTableWatcher(
      api.purchaseOrderHistory,
      purchaseOrderHistoryPivotTableKey,
      [
        types.PURCHASING_PURCHASE_ORDER_SAVE_RECEIVED,
        types.PURCHASING_PURCHASE_ORDER_CHANGE_STATUS_SUCCESS,
      ],
      state => {
        const purchaseOrder = getSelectedPurchaseOrder(state)
        return {
          purchaseOrderId: [
            get(purchaseOrder, 'id', null),
          ],
        }
      },
      {
        default: true,
      }
  )


  const addItemsToPurchaseOrderDialogOrdersPivotTable = createPivotTableWatcher(
      api.quote,
      addItemsToPurchaseOrderDialogOrdersPivotTableKey,
      [
        types.PURCHASING_ADD_ITEMS_TO_PURCHASE_ORDER_DIALOG_UPDATE_FILTERS,
      ],
      state => {
        const invoiceFinancialYearStart = getInvoiceFinancialYearStart(state)
        const filters = getAddItemsToPurchaseOrderDialog(state)
        const {
          statusTypes, companies,
          contacts, dateTypes,
        } = filters
        const {targetStart, targetEnd} = getDateFilters(filters, invoiceFinancialYearStart)

        return getPurchasingFilterParams(
            getSearch(state, addItemsToPurchaseOrderDialogOrdersPivotTableKey),
            statusTypes,
            companies,
            contacts,
            dateTypes,
            targetStart,
            targetEnd)
      },
      quotePivotTableFields)


  const addItemsToPurchaseOrderDialogOrderItemsPivotTable = createPivotTableWatcher(
      api.quoteItem,
      addItemsToPurchaseOrderDialogItemsPivotTableKey,
      [],
      state => {
        return {
          orderId: state.dialogs.addItemsToPurchaseOrder.selectedOrderId,
        }
      },
      {
        default: true,
        part: true,
        purchaseOrderItems: true,
        subitems: {
          default: true,
          part: true,
          purchaseOrderItems: true,
        },
      })

  const purchaseOrderFinderOrdersPivotTable = createPivotTableWatcher(
      api.purchaseOrder,
      purchasingContextSelectorOrdersPivotTableKey,
      [
        types.PURCHASING_PURCHASE_ORDER_FINDER_UPDATE_FILTERS,
      ],
      state => {
        const invoiceFinancialYearStart = getInvoiceFinancialYearStart(state)
        const filters = getPurchaseOrderSelector(state)

        const {statusTypes, companies, contacts, dateTypes} = filters
        const {targetStart, targetEnd} = getDateFilters(filters, invoiceFinancialYearStart)

        return getPurchasingFilterParams(
            getSearch(state, purchasingContextSelectorOrdersPivotTableKey),
            statusTypes,
            companies,
            contacts,
            dateTypes,
            targetStart,
            targetEnd)
      },
      quotePivotTableFields)

  const purchaseOrderFinderItemsPivotTable = createPivotTableWatcher(
      api.purchaseOrderItem,
      purchasingContextSelectorOrderItemsPivotTableKey,
      [],
      state => {
        return {
          purchaseOrderId: getPurchaseOrderSelector(state).selectedOrderId,
        }
      },
      {
        default: true,
        purchaseOrder: {
          currencyId: true,
        },
        userfields: true,
      })

  const purchaseInvoiceItemsPivotTable = createPivotTableWatcher(
      api.purchaseInvoiceItem,
      purchasingContextPurchaseInvoiceItemsPivotTableKey,
      [],
      state => {
        return {
          purchaseInvoiceId: getSelectedPurchaseInvoice(state).id,
        }
      },
      {
        default: true,
      }
  )

  function* watcher() {
    yield fork(myWatcher)
    yield fork(purchaseOrdersPivotTable)
    yield fork(purchaseOrderItemsPivotTable)
    yield fork(purchaseOrderDocumentsPivotTable)
    yield fork(purchaseOrderNewDocumentsPivotTable)
    yield fork(purchaseOrderInvoicesPivotTable)
    yield fork(purchaseOrderHistoryPivotTable)
    yield fork(addItemsToPurchaseOrderDialogOrdersPivotTable)
    yield fork(addItemsToPurchaseOrderDialogOrderItemsPivotTable)
    yield fork(purchaseOrderFinderOrdersPivotTable)
    yield fork(purchaseOrderFinderItemsPivotTable)
    yield fork(purchaseInvoiceItemsPivotTable)
  }

  return {
    watcher,
  }
}
